<template>
  <div>
    <template v-if="!!errors.length">
      <BaseAlert
        v-for="(error, key) in errors"
        :key="`bdx-wizard-error-${key}`"
        variant="danger"
        class="py-2 px-3"
        >{{ error }}</BaseAlert
      >
    </template>

    <SlideXRightTransition group>
      <component
        v-for="(validStep, key) in validSteps"
        v-if="activeStep === key"
        v-model="columns[validStep.model]"
        :key="`bdx-wizard-step-${validStep.component}`"
        :is="validStep.component"
      ></component>
    </SlideXRightTransition>

    <div class="mt-4 d-flex align-items-center justify-content-between">
      <BDXWizardBackButton @cancel="() => $emit('cancel')" />
      <BDXWizardSaveButton />
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  computed,
  provide,
  ref,
  toRefs
} from '@vue/composition-api'
import { SlideXRightTransition } from 'vue2-transitions'
import { difference } from 'lodash-es'

import { BaseAlert } from '@components/base'
import {
  RLSUnderwriterLeadsChecklist,
  RLSUnderwriterFollowersChecklist
} from '@components/rls'

import BDXWizardTPANames from './BDXWizardTPANames.vue'
import BDXWizardCoverholders from './BDXWizardCoverholders.vue'
import BDXWizardBrokers from './BDXWizardBrokers.vue'
import BDXWizardCurrencies from './BDXWizardCurrencies.vue'

import BDXWizardBackButton from './BDXWizardBackButton.vue'
import BDXWizardSaveButton from './BDXWizardSaveButton.vue'

export default defineComponent({
  name: 'BDXWizard',
  components: {
    SlideXRightTransition,

    BaseAlert,

    RLSUnderwriterLeadsChecklist,
    RLSUnderwriterFollowersChecklist,

    BDXWizardTPANames,
    BDXWizardCoverholders,
    BDXWizardBrokers,
    BDXWizardCurrencies,

    BDXWizardBackButton,
    BDXWizardSaveButton
  },
  props: {
    id: {
      type: [Number, Array],
      default: () => [],
      required: true
    },
    value: {
      type: Object,
      default: () => ({
        TPA_Name: [],
        Underwriter_Lead: [],
        Underwriter_Follower: [],
        Coverholder_Name: [],
        Broker: undefined,
        Currency: undefined
      }),
      validator: (value) => {
        const diff = difference(
          [
            'TPA_Name',
            'Underwriter_Lead',
            'Underwriter_Follower',
            'Coverholder_Name',
            'Broker',
            'Currency'
          ],
          Object.keys(value)
        )

        return diff.length === 0
      },
      required: true
    },
    excludeSteps: {
      type: Array,
      default: () => []
    }
  },
  emits: ['input', 'cancel', 'saved'],
  setup(props, { emit }) {
    const { id } = toRefs(props)

    const columns = computed({
      get: () => props.value,
      set: (value) => emit('input', value)
    })

    provide('bdx-wizard', {
      id,
      columns,

      emit
    })

    // Step wizard
    const activeStep = ref(0)

    const setActiveStep = (newActiveStep) => (activeStep.value = newActiveStep)

    const validSteps = computed(() => {
      return [
        { component: 'BDXWizardTPANames', model: 'TPA_Name' },
        {
          component: 'RLSUnderwriterLeadsChecklist',
          model: 'Underwriter_Lead'
        },
        {
          component: 'RLSUnderwriterFollowersChecklist',
          model: 'Underwriter_Follower'
        },
        {
          component: 'BDXWizardCoverholders',
          model: 'Coverholder_Name'
        },
        { component: 'BDXWizardBrokers', model: 'Broker' },
        { component: 'BDXWizardCurrencies', model: 'Currency' }
      ].filter((stepItem) => !props.excludeSteps.includes(stepItem.component))
    })

    provide('bdx-wizard--steps', {
      activeStep,
      setActiveStep,

      validSteps
    })

    // Errors
    const errors = ref([])

    const setErrors = (newErrors) => (errors.value = newErrors)

    provide('bdx-wizard--errors', {
      errors,
      setErrors
    })

    return {
      columns,

      activeStep,
      validSteps,

      errors
    }
  }
})
</script>
